import { AddTestForm, ColumnResource, TimestampFormField } from 'shared/types';

export const getSharedTimestampColumnNames = (
  columnsResourcesData: ColumnResource[],
  resourceIds: string[],
): string[] => {
  const selectedIds = new Set(resourceIds);
  if (!selectedIds.size) {
    return [];
  }
  const tablesByColumnName = columnsResourcesData.reduce<
    Record<string, Set<string>>
  >((acc, col) => {
    if (col.type === 'timestamp' && selectedIds.has(col.model_id)) {
      (acc[col.name] ??= new Set()).add(col.model_id);
    }
    return acc;
  }, {});

  return Object.entries(tablesByColumnName)
    .filter(([, tableIds]) => tableIds.size === selectedIds.size)
    .map(([name]) => name)
    .sort((a, b) => a.localeCompare(b));
};

const NO_TIMESTAMP_COLUMN_MESSAGES = {
  single: {
    required: 'This table has no timestamp column. Select a different table.',
    optional:
      'This table has no timestamp column, so the test runs without one',
  },
  multiple: {
    required:
      'The selected tables have no timestamp column in common. Select different tables.',
    optional:
      'The selected tables have no timestamp column in common, so the test runs without one',
  },
};

export const getNoTimestampColumnMessage = (
  tableCount: number,
  isRequired: boolean,
): string =>
  NO_TIMESTAMP_COLUMN_MESSAGES[tableCount > 1 ? 'multiple' : 'single'][
    isRequired ? 'required' : 'optional'
  ];

export const TIME_BUCKET_ONLY_REASON =
  'Only applies when a timestamp column is selected';

export const TIME_BUCKET_ONLY_FIELDS = new Set([
  'detectionPeriod',
  'detectionUnit',
  'backfillDays',
]);

const TIMESTAMP_FIELD_REQUIREMENTS: Record<
  TimestampFormField,
  Partial<Record<AddTestForm['testType'], 'required' | 'optional'>>
> = {
  timestampColumn: {
    freshnessAnomalies: 'required',
    eventFreshnessAnomalies: 'required',
    volumeAnomalies: 'optional',
    dimensionAnomalies: 'optional',
  },
  updatedAtTimestampColumn: {
    eventFreshnessAnomalies: 'optional',
  },
};

const isTimestampFormField = (field: string): field is TimestampFormField =>
  field === 'timestampColumn' || field === 'updatedAtTimestampColumn';

export const getTimestampFieldRequirement = (
  field: string,
  testType: AddTestForm['testType'] | undefined,
) =>
  isTimestampFormField(field) && testType
    ? TIMESTAMP_FIELD_REQUIREMENTS[field][testType]
    : undefined;

export const usesTimeBuckets = (
  testType: AddTestForm['testType'] | undefined,
  timestampColumn: string | undefined,
): boolean =>
  getTimestampFieldRequirement('timestampColumn', testType) !== 'optional' ||
  !!timestampColumn;

export const getWizardTimestampFieldOptions = (
  testType: AddTestForm['testType'] | undefined,
  timestampColumns: string[],
): Partial<
  Record<TimestampFormField, { options: string[]; allowsNone: boolean }>
> =>
  Object.entries(TIMESTAMP_FIELD_REQUIREMENTS).reduce<
    Partial<
      Record<TimestampFormField, { options: string[]; allowsNone: boolean }>
    >
  >((acc, [field, requirements]) => {
    const requirement = testType ? requirements[testType] : undefined;
    if (!requirement || !isTimestampFormField(field)) {
      return acc;
    }
    return {
      ...acc,
      [field]: {
        options: timestampColumns,
        allowsNone: requirement === 'optional',
      },
    };
  }, {});

export const normalizeWizardTimestampFieldWrite = ({
  field,
  value,
  testType,
  timestampColumns,
}: {
  field: string;
  value: unknown;
  testType: AddTestForm['testType'] | undefined;
  timestampColumns: string[];
}): { ok: true; value: unknown } | { ok: false; reason: string } => {
  const requirement = getTimestampFieldRequirement(field, testType);
  if (!requirement) {
    return { ok: true, value };
  }
  const optionsText = timestampColumns.length
    ? timestampColumns.join(', ')
    : 'none';
  if (value === null || value === '') {
    return requirement === 'optional'
      ? { ok: true, value: '' }
      : {
          ok: false,
          reason: `Required for this test. Options: ${optionsText}`,
        };
  }
  if (typeof value !== 'string' || !timestampColumns.includes(value)) {
    return {
      ok: false,
      reason: `Not a timestamp column shared by the selected tables. Options: ${optionsText}${
        requirement === 'optional' ? ', or "" for no column' : ''
      }`,
    };
  }
  return { ok: true, value };
};
